import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  Box
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import { formEditorService } from '../../services/formEditorService';
import { extractApiErrorMessage } from '../../utils/apiUtils';

interface UpdateSqlGeneratorDialogProps {
  open: boolean;
  formId: number;
  formDescription: string;
  onClose: () => void;
}

export const UpdateSqlGeneratorDialog: React.FC<UpdateSqlGeneratorDialogProps> = ({
  open,
  formId,
  formDescription,
  onClose
}) => {
  const [sql, setSql] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  React.useEffect(() => {
    if (open && formId) {
      generateSql();
    }
  }, [open, formId]);

  const generateSql = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await formEditorService.generateUpdateSql(formId);
      setSql(result);
    } catch (err: any) {
      setError(extractApiErrorMessage(err, 'Failed to generate UPDATE SQL'));
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(sql);
      setCopySuccess(true);
    } catch (err) {
      console.error('Failed to copy to clipboard', err);
    }
  };

  const handleClose = () => {
    setSql('');
    setError('');
    setCopySuccess(false);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { height: '80vh' }
        }}
      >
        <DialogTitle>
          Generate UPDATE SQL for {formDescription}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500]
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <TextField
              multiline
              fullWidth
              value={sql}
              InputProps={{
                readOnly: true,
                sx: {
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  height: '100%',
                  alignItems: 'flex-start'
                }
              }}
              sx={{
                height: '100%',
                '& .MuiInputBase-root': {
                  height: '100%'
                }
              }}
            />
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopyToClipboard}
            disabled={!sql || loading}
          >
            Copy to Clipboard
          </Button>
          <Button variant="contained" onClick={handleClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess(false)}
        message="UPDATE SQL copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};
